/**
 * @file src/shell/Shell.js
 * Lắp toàn bộ UI dùng chung và cấp `ctx.ui` cho tiết.
 *
 * LUẬT BẤT DI BẤT DỊCH: file này (và mọi file trong src/shell/) KHÔNG được chứa
 * tên của bất kỳ tiết nào. Nếu cần một UI đặc thù, tiết tự đăng ký qua ctx.ui.*.
 * Đây là điều sửa lại so với three-lessons, nơi popup "Hải lưu" nằm trong
 * UIManager dùng chung. Xem registry.js để biết lý do đầy đủ.
 */

import { createRegistry } from './registry.js';
// Bố cục: HEADER cố định ở đỉnh (Header.js) · thanh BƯỚC của tiết ở đáy (StepBar) ·
// cột CÔNG CỤ dọc mép phải (ToolRail) · thanh zoom dọc mép trái (ZoomSlider).
// SHELL_BUTTON_IDS theo nút nên nằm cùng ToolRail.js.
import { createHeader } from './Header.js';
import { createPenLayer } from './PenLayer.js';
import { createWhiteboard } from './Whiteboard.js';
import { createStepBar } from './StepBar.js';
import { createAnimBar } from './AnimBar.js';
import { createToolRail, SHELL_BUTTON_IDS } from './ToolRail.js';
import { createZoomSlider } from './ZoomSlider.js';
import { createLoadingScreen } from './LoadingScreen.js';
import { createInfoPanel } from './InfoPanel.js';
import { createSettingsPanel } from './SettingsPanel.js';
import { createExercisePanel } from './ExercisePanel.js';
import { createLabelSystem } from './Label.js';
import { showErrorScreen } from './ErrorScreen.js';
import { icon } from './icons.js';
import './shell.css';

/**
 * API mà tiết dùng để cắm UI riêng vào taskbar/panel CHUNG.
 * @typedef {Object} UiApi
 * @property {(o: {id: string, icon: string, labelKey: string, order?: number, toggle?: boolean,
 *   state?: boolean, initial?: boolean, disabled?: boolean, onClick: (state?: boolean) => void}) =>
 *   {setState:(v:boolean)=>void, setEnabled:(v:boolean)=>void, remove:()=>void}} addButton
 * @property {(o: {id: string, labelKey: string, min: number, max: number, step?: number,
 *   value: number, icon?: string, order?: number, onChange: (v: number) => void}) =>
 *   {setValue:(v:number)=>void, remove:()=>void}} addSlider
 * @property {(o: {id: string, labelKey: string, subLabelKey?: string, icon?: string,
 *   order?: number, onSelect: (id: string, again: boolean) => void}) =>
 *   {update:(patch: Record<string, any>)=>void, remove:()=>void}} addTab
 * @property {(o: {id: string, titleKey: string, bodyKey: string, order?: number}) => {remove:()=>void}} addInfoSection
 * @property {(o: {duration: number, playing?: boolean, mode?: 'play'|'stages',
 *   stages?: number[], onToggle: () => void, onSeek: (t: number) => void} | null) => void} setAnimation
 * @property {(playing: boolean) => void} setAnimationPlaying
 * @property {(t: number) => void} setAnimationProgress
 * @property {(o: {id: string, labelKey: string, type: 'toggle'|'select',
 *   options?: any[], value: unknown, order?: number, onChange: (v: unknown) => void}) => {remove:()=>void}} addSetting
 * @property {(o: {id: string, titleKey: string, icon?: string, draggable?: boolean,
 *   items: Array<{value: string, labelKey: string}>, value?: string,
 *   onSelect: (value: string) => void}) => PopupHandle} addPopup
 * @property {(o: {key: string, text?: string, follow?: import('three').Object3D,
 *   position?: [number,number,number], offset?: [number,number,number], variant?: string}) =>
 *   import('./Label.js').LabelHandle} label
 * @property {(id: string, h: {show: () => void, hide: () => void}) => () => void} registerTextGroup
 * @property {(o: {messageKey: string, kind?: 'info'|'warn', ms?: number}) => void} toast
 * @property {(on: boolean) => void} setCompact
 * @property {() => boolean} isTextVisible
 * @property {(on: boolean) => void} setStepsBusy
 * @property {(source: {fetchQuestions: () => Promise<import('./ExercisePanel.js').QuizQuestion[]>} | null) => void} setQuiz
 * @property {(patch: Record<string, any>) => void} setLessonInfo
 */

/**
 * @typedef {Object} PopupHandle
 * @property {() => void} open
 * @property {() => void} close
 * @property {() => boolean} isOpen
 * @property {(value: string) => void} setValue
 * @property {() => void} remove
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {HTMLElement} o.stage
 * @param {any} o.engine
 * @param {(k: string, v?: any) => string} o.t
 * @param {import('../core/settings.js').Settings} o.settings
 * @param {import('../core/events.js').Emitter} o.emitter
 * @param {Record<string, any>} o.info
 * @param {string[]} o.languages
 * @param {() => void} o.onResetView
 * @param {(on: boolean) => void} o.onToggleRotate
 */
export function mountShell(o) {
  // is-booting → các khối chrome (header/thanh bước/cột công cụ/zoom) trôi vào chỗ;
  // markReady() gỡ, SAU khi màn tải tắt hẳn.
  // KHÔNG có cờ thứ hai cho sân khấu 3D: mô hình KHÔNG fade lúc mở bài. Từng có
  // `is-stage-hidden` cho việc đó, đã bỏ — cảnh 3D đã render xong sau lớp phủ tải rồi
  // nên fade lại nó vừa thừa vừa sinh lỗi hiển thị (mô hình bạc màu / nền trống trắng
  // mờ). Xem mục "Nhịp MỞ BÀI" trong shell.css để biết cả ba biến thể đã thử và vì sao
  // bỏ, kèm chỗ đúng để làm hiệu ứng tan/hiện khi ĐỔI BƯỚC (ctx.engine.fadeObject()).
  o.root.classList.add('is-booting');

  // Chuyển cảnh khi đổi bước KHÔNG làm ở tầng shell. Trước đây ở đây có
  // crossfadeStage() mờ cả .ol-stage (canvas + nhãn) rồi mờ lại. Đã BỎ: mờ cả sân khấu
  // thì nền và nhãn cũng tối theo, cảm giác như một lớp phủ đè lên màn hình. Lối của
  // Mozaik 3D — và của bản three-lessons cũ — là fade TỪNG MODEL, nền giữ nguyên. Việc
  // đó thuộc về tiết, dùng ctx.engine.fadeObject(). Xem setMode() của tiết hải lưu.

  const buttons = createRegistry({ name: 'ui.buttons', reservedIds: SHELL_BUTTON_IDS });
  const sliders = createRegistry({ name: 'ui.sliders' });
  const tabs = createRegistry({ name: 'ui.tabs' });
  const sections = createRegistry({ name: 'ui.infoSections' });
  const extraSettings = createRegistry({ name: 'ui.settings' });

  // Lớp vẽ tự do + bảng trắng — tạo TRƯỚC header vì header cần callback trỏ vào chúng
  // ngay khi dựng. whiteboard.onClose lại cần gọi NGƯỢC vào header (đồng bộ nút × của
  // panel với nút ở Header) nên phải tham chiếu qua một biến gán SAU khi cả hai đã tạo
  // — cùng lối bInfo/bSettings ở dưới, hai bên tham chiếu chéo nhau.
  /** @type {ReturnType<typeof createHeader> | null} */
  let headerRef = null;
  // ExercisePanel thì NGƯỢC LẠI với whiteboard: nó cần panelHost (tạo ở dưới, sau
  // toolRail/zoom) nên không thể tạo trước header như penLayer/whiteboard. Header vẫn
  // cần callback trỏ vào nó ngay lúc dựng → tham chiếu qua biến gán SAU, cùng lối
  // headerRef ở trên (một bên tạo trước phải chờ, bên kia tạo sau phải có chỗ trỏ vào
  // trước khi tồn tại).
  /** @type {ReturnType<typeof createExercisePanel> | null} */
  let exercisePanelRef = null;
  const penLayer = createPenLayer({
    stage: o.stage,
    t: o.t,
    getPenSlot: () => headerRef?.getPenSlot(),
  });
  const whiteboard = createWhiteboard({
    stage: o.stage,
    t: o.t,
    onClose: () => headerRef?.setWhiteboardActive(false),
  });

  // Header đọc title từ o.info (content/index/<môn>.json) — CÙNG nguồn mà loading
  // screen và InfoPanel đã dùng, xem chú thích đầu Header.js về lý do KHÔNG lấy từ
  // config.js của tiết.
  const header = createHeader({
    root: o.root,
    t: o.t,
    info: o.info,
    lang: () => String(o.settings.get('lang')),
    onTogglePen: (on) => penLayer.setActive(on),
    onToggleWhiteboard: (on) => whiteboard.setOpen(on),
    onToggleExercise: (on) => (on ? exercisePanelRef?.open() : exercisePanelRef?.close()),
  });
  headerRef = header;

  const stepBar = createStepBar({
    root: o.root,
    t: o.t,
    tabs,
    emitter: o.emitter,
    // Logo Onluyen. Đường dẫn TUYỆT ĐỐI từ gốc site, không qua ctx.assets: đây là asset
    // của SHELL (mọi tiết đều dùng) nên nó không thuộc manifest/ngân sách của tiết nào.
    logoUrl: '/assets/_logo/Edmicro_logo.png',
    logoAlt: 'Onluyen',
  });
  // root: o.stage (KHÔNG phải o.root) — xem chú thích đầu Whiteboard.js: cột công cụ
  // phải neo theo khung 3D đang co giãn, không phải theo toàn màn hình.
  const toolRail = createToolRail({ root: o.stage, t: o.t, buttons, sliders });
  // Thanh animation: dựng MỘT lần, ẩn sẵn. Tiết bật/tắt qua ui.setAnimation() mỗi lần
  // đổi bước — dựng lại DOM mỗi bước thì hoạt ảnh xuất hiện chạy lại và thanh nhấp nháy.
  /** @type {{onToggle: () => void, onSeek: (t: number) => void} | null} */
  let animCbs = null;
  const animBar = createAnimBar({
    stage: o.stage,
    t: o.t,
    onToggle: () => animCbs?.onToggle(),
    onSeek: (v) => animCbs?.onSeek(v),
  });

  const loading = createLoadingScreen(o.root);
  const labelSystem = createLabelSystem({ stage: o.stage, engine: o.engine, t: o.t });

  // ---------- Thanh zoom dọc bên phải ----------
  // Biên zoom do Engine._applyZoomLock đặt (maxDistance = khoảng cách lúc frameObject,
  // minDistance = max * minDistanceRatio). Nếu tiết chưa gọi frameObject/resetView thì
  // OrbitControls vẫn ở mặc định 0..Infinity → phải tự suy biên, không thì ra NaN.
  function zoomBounds() {
    const c = o.engine.controls;
    const cur = o.engine.camera.position.distanceTo(c.target);
    const min = Number.isFinite(c.minDistance) && c.minDistance > 0 ? c.minDistance : cur * 0.5;
    const max = Number.isFinite(c.maxDistance) ? c.maxDistance : cur * 2;
    return { min, max: Math.max(max, min + 1e-6), cur };
  }

  const zoom = createZoomSlider({
    root: o.stage, // lý do giống toolRail ở trên
    t: o.t,
    onInput: (t01) => {
      const { min, max } = zoomBounds();
      o.engine.setZoomTargetDistance(max - t01 * (max - min)); // t01 = 1 → gần nhất
    },
  });

  // ---------- Cờ nền tối — CHỈ cho chỉ báo zoom ----------
  // Chrome (thanh bước · cột công cụ · panel) KHÔNG đổi theo nền — đó là yêu cầu rõ
  // ràng và class `.is-bg-dark` cũ đã bỏ hẳn cùng cả bộ token tối của nó.
  // RIÊNG chỉ báo zoom thì phải đổi: nó là chấm + vạch vẽ TRẦN trên sân khấu, không có
  // khay nền đỡ, nên màu xanh #1c398e đặt trên nền đen gần như biến mất.
  // Tên class cố ý KHÁC (`is-stage-dark`, không phải `is-bg-dark`) để không ai tưởng
  // chủ đề tối cũ quay lại và treo thêm rule vào đó.
  /** @param {unknown} hex @returns {boolean} true nếu nền TỐI */
  function isStageDark(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex ?? '').trim());
    if (!m) return false; // không đọc được → coi như nền sáng (mặc định của hệ thống)
    const n = parseInt(m[1] ?? '0', 16);
    /** @param {number} v kênh 0–255 → giá trị tuyến tính */
    const lin = (v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    return L < 0.4; // ngưỡng cao hơn 0.22 của bản cũ: đây chỉ là 2 chấm mảnh, nền xám
    // trung tính cũng đã đủ nuốt mất màu xanh đậm.
  }
  const applyStageTheme = () =>
    o.root.classList.toggle('is-stage-dark', isStageDark(o.settings.get('background')));
  applyStageTheme();
  const offStageTheme = o.settings.onChange('background', applyStageTheme);

  // Nhãn thương hiệu chữ ("EDMICRO 3D" dựng bằng <span> + <b>) ĐÃ BỎ: giờ là ảnh logo
  // Onluyen nằm ở mép phải thanh bước, do StepBar tự dựng (xem logoUrl ở trên). Nhờ
  // vậy hết luôn khối code đổi màu chữ theo độ sáng nền — ảnh có màu riêng.

  // Chiều ngược: lăn chuột / pinch / resetView đổi camera → thanh chạy theo.
  // Chỉ ghi DOM khi lệch đủ lớn, tránh set value mỗi frame.
  let lastShown = -1;
  const offZoomSync = o.engine.onUpdate(() => {
    const { min, max, cur } = zoomBounds();
    const t01 = Math.max(0, Math.min(1, (max - cur) / (max - min)));
    if (Math.abs(t01 - lastShown) < 0.004) return;
    lastShown = t01;
    zoom.setValue(t01);
  });

  // Ba panel dùng chung một chỗ ở mép phải nên chỉ được mở MỘT cái; mở cái này thì
  // đóng (tối đa) hai cái kia. Trạng thái mở đẩy ngược ra nút bật nó: Info/Settings ra
  // nút taskbar (bInfo/bSettings), Exercise ra nút ở HEADER (headerRef.setExerciseActive
  // — không phải nút taskbar, xem Header.js). Gán handle sau khi tạo xong — ba panel
  // tham chiếu chéo nhau (đóng nhau) nên không thể tất cả đợi nhau theo đúng thứ tự.
  /** @type {{setState: (v: boolean) => void} | null} */
  let bInfo = null;
  /** @type {{setState: (v: boolean) => void} | null} */
  let bSettings = null;

  // Khung CẮT cho hai panel. Panel trượt ngang bên trong khung này, và khung được đặt
  // sao cho mép phải của nó trùng mép trái của cột công cụ — nhờ vậy panel lộ ra "từ
  // dưới cột công cụ" chứ không bay từ ngoài mép màn hình vào.
  // Trước đây panel là con trực tiếp của root và trạng thái đóng đẩy nó ra hẳn ngoài
  // khung (translateX vượt cả bề rộng cột), nên nó xuất phát từ mép phải màn hình.
  const panelHost = document.createElement('div');
  panelHost.className = 'ol-panelhost';
  o.stage.appendChild(panelHost); // lý do giống toolRail/zoom ở trên

  const infoPanel = createInfoPanel({
    root: panelHost,
    t: o.t,
    info: o.info,
    lang: () => String(o.settings.get('lang')),
    sections,
    onOpenChange: (open) => {
      bInfo?.setState(open);
      if (open) {
        settingsPanel.close();
        exercisePanel.close();
      }
    },
  });
  const settingsPanel = createSettingsPanel({
    root: panelHost,
    t: o.t,
    settings: o.settings,
    languages: o.languages,
    extra: extraSettings,
    onOpenChange: (open) => {
      bSettings?.setState(open);
      if (open) {
        infoPanel.close();
        exercisePanel.close();
      }
    },
  });
  // Panel "Bài tập" (ExercisePanel.js) — TEMPLATE trắc nghiệm chung, xem đầu file đó.
  // Nội dung câu hỏi do TIẾT cấp qua ctx.ui.setQuiz() (xem UiApi.setQuiz bên dưới),
  // panel này không biết gì về nội dung — có thể mở lên trống (quiz.empty) nếu tiết
  // chưa gọi setQuiz().
  const exercisePanel = createExercisePanel({
    root: panelHost,
    t: o.t,
    onOpenChange: (open) => {
      headerRef?.setExerciseActive(open);
      if (open) {
        infoPanel.close();
        settingsPanel.close();
      }
    },
  });
  exercisePanelRef = exercisePanel;

  // Tiêu đề tiết KHÔNG còn hiện trên thanh: tên bài đã có ở trang cha (front-end)
  // và trong panel "Thông tin bài giảng". o.info.title vẫn được dùng ở hai chỗ đó.

  // ======================================================================
  // 5 nút CỦA SHELL — tiết không phải viết dòng nào
  // ======================================================================
  // Nút xoay: NGUỒN SỰ THẬT là settings.rotate, y như nút âm thanh. Nút chỉ ghi vào
  // settings; việc vẽ lại nút và phát 'lesson:rotate' cho tiết do listener bên dưới lo.
  // Nhờ vậy đổi từ taskbar hay từ bảng cài đặt đều ra cùng một kết quả.
  const bRotate = buttons.add(
    'rotate',
    {
      icon: 'rotate',
      labelKey: 'btn.rotate',
      toggle: true,
      state: Boolean(o.settings.get('rotate')),
      onClick: () => o.settings.set('rotate', !o.settings.get('rotate')),
    },
    0,
  );
  const offRotate = o.settings.onChange('rotate', (v) => {
    bRotate.update({ state: Boolean(v) });
    o.onToggleRotate(Boolean(v));
  });
  // KHÔNG phát onToggleRotate ở đây: mountShell() chạy TRƯỚC lesson.init(), listener
  // của tiết chưa tồn tại nên event đầu tiên sẽ rơi vào hư không. Tiết tự đọc
  // ctx.settings.get('rotate') làm giá trị ban đầu — settings là nguồn sự thật.

  // Nút "đặt lại góc nhìn" đã BỎ theo yêu cầu: ba icon mũi tên vòng (xoay · đặt lại ·
  // phát lại của tiết) trông giống nhau, chỉ giữ lại nút xoay. o.onResetView vẫn được
  // giữ trong API để tiết/FE gọi được, và ctx.controls.saveState() vẫn cần cho
  // ctx.engine.resetView(). Muốn hiện lại thì thêm buttons.add('reset', …) ở đây.

  const bLabels = buttons.add(
    'labels',
    {
      icon: 'label',
      labelKey: 'btn.labels',
      toggle: true,
      state: true,
      onClick: () => {
        const next = !labelSystem.isTextVisible();
        labelSystem.setTextVisible(next);
        // Đổi luôn icon: chữ T gạch chéo khi đang ẩn — cùng lối với sound/mute,
        // rõ hơn là chỉ làm mờ nút.
        bLabels.update({ state: next, icon: next ? 'label' : 'labelOff' });
      },
    },
    2,
  );

  const bSound = buttons.add(
    'sound',
    {
      icon: o.settings.get('sound') ? 'sound' : 'mute',
      labelKey: 'btn.sound',
      toggle: true,
      state: Boolean(o.settings.get('sound')),
      // CHỈ ghi vào settings, KHÔNG tự cập nhật nút. Việc vẽ lại nút do listener
      // bên dưới lo — nhờ vậy nút ở taskbar và hàng trong bảng cài đặt luôn khớp
      // nhau, dù đổi từ phía nào. Trước đây nút tự cập nhật nên đổi trong bảng
      // cài đặt thì icon ở taskbar vẫn đứng nguyên.
      onClick: () => o.settings.set('sound', !o.settings.get('sound')),
    },
    3,
  );
  const offSound = o.settings.onChange('sound', (v) =>
    bSound.update({ state: Boolean(v), icon: v ? 'sound' : 'mute' }),
  );

  // toggle: true → nút có trạng thái sáng như nút xoay / ẩn-hiện chữ / âm thanh.
  // Trạng thái KHÔNG do onClick tự đặt mà do onOpenChange của panel đẩy về, nên đóng
  // panel bằng dấu × bên trong cũng làm nút tắt sáng theo.
  const hInfo = buttons.add(
    'info',
    {
      icon: 'info',
      labelKey: 'btn.info',
      toggle: true,
      state: false,
      onClick: () => infoPanel.toggle(),
    },
    4,
  );
  const hSettings = buttons.add(
    'settings',
    {
      icon: 'settings',
      labelKey: 'btn.settings',
      toggle: true,
      state: false,
      onClick: () => settingsPanel.toggle(),
    },
    5,
  );
  bInfo = { setState: (v) => hInfo.update({ state: v }) };
  bSettings = { setState: (v) => hSettings.update({ state: v }) };

  // Nút "Toàn màn hình" ĐÃ BỎ: thiết kế mới chốt đúng 5 ô ở thanh dưới (xoay ·
  // ẩn nhãn · âm thanh · thông tin · cài đặt). Toàn màn hình là việc của TRANG CHA
  // — tiết nằm trong iframe, requestFullscreen từ trong iframe cần allow="fullscreen"
  // nên vốn đã không đáng tin. Muốn hiện lại thì thêm buttons.add('fullscreen', …)
  // ở đây VÀ thêm 'fullscreen' vào SHELL_BUTTON_IDS trong BottomBar.js.

  // ======================================================================
  // Popup động — thay cho oceanPopup viết cứng ở bản cũ
  // ======================================================================
  // Giữ luôn hàm dịch lại của từng popup: retranslate() của shell phải chạm tới
  // popup nữa, không thì đổi sang tiếng Anh mà popup của tiết vẫn nguyên tiếng Việt.
  /** @type {Map<string, {el: HTMLElement, cleanup: () => void, retranslate: () => void}>} */
  const popups = new Map();

  /** @type {UiApi['addPopup']} */
  function addPopup(cfg) {
    const el = document.createElement('div');
    el.className = 'ol-popup is-hidden';
    el.setAttribute('role', 'menu');
    el.innerHTML = `
      <div class="ol-popup__head">
        <span class="ol-icon">${icon(cfg.icon ?? 'eye')}</span>
        <span class="ol-popup__title"></span>
        <button class="ol-popup__close"><span class="ol-icon">${icon('close')}</span></button>
      </div>
      <div class="ol-popup__items"></div>`;
    const titleEl = /** @type {HTMLElement} */ (el.querySelector('.ol-popup__title'));
    const syncTitle = () => {
      titleEl.textContent = o.t(cfg.titleKey);
    };
    syncTitle();

    const itemsEl = /** @type {HTMLElement} */ (el.querySelector('.ol-popup__items'));
    let value = cfg.value ?? cfg.items[0]?.value ?? '';

    const renderItems = () => {
      itemsEl.innerHTML = '';
      for (const it of cfg.items) {
        const b = document.createElement('button');
        b.className = 'ol-popup__item';
        b.textContent = o.t(it.labelKey);
        b.classList.toggle('is-active', it.value === value);
        b.addEventListener('click', () => {
          value = it.value;
          renderItems();
          cfg.onSelect(value); // KHÔNG tự đóng popup — giữ hành vi của bản cũ
        });
        itemsEl.appendChild(b);
      }
    };
    renderItems();

    /** @type {HTMLElement} */ (el.querySelector('.ol-popup__close')).addEventListener(
      'click',
      () => el.classList.add('is-hidden'),
    );

    o.stage.appendChild(el);
    const cleanupDrag = cfg.draggable === false ? () => {} : enableDrag(el, cfg.id);
    popups.set(cfg.id, {
      el,
      cleanup: cleanupDrag,
      retranslate: () => {
        syncTitle();
        renderItems();
      },
    });

    return {
      open: () => el.classList.remove('is-hidden'),
      close: () => el.classList.add('is-hidden'),
      isOpen: () => !el.classList.contains('is-hidden'),
      setValue: (v) => {
        value = v;
        renderItems();
      },
      remove: () => {
        cleanupDrag();
        el.remove();
        popups.delete(cfg.id);
      },
    };
  }

  /** Kéo popup + ghi nhớ vị trí. Rút từ _enableOceanPopupDrag của bản cũ, tổng quát hoá. */
  function enableDrag(/** @type {HTMLElement} */ el, /** @type {string} */ id) {
    const head = /** @type {HTMLElement} */ (el.querySelector('.ol-popup__head'));
    const LS = `onluyen3d:popup:${id}`;
    try {
      const p = JSON.parse(localStorage.getItem(LS) ?? 'null');
      if (p && typeof p.x === 'number') {
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        el.style.bottom = 'auto';
      }
    } catch {
      /* ignore */
    }

    let ox = 0;
    let oy = 0;
    /** @param {PointerEvent} e */
    const onMove = (e) => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const x = Math.max(0, Math.min(window.innerWidth - w, e.clientX - ox));
      const y = Math.max(0, Math.min(window.innerHeight - h, e.clientY - oy));
      el.style.left = `${Math.round(x)}px`;
      el.style.top = `${Math.round(y)}px`;
    };
    const onUp = () => {
      el.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onMove);
      const r = el.getBoundingClientRect();
      try {
        localStorage.setItem(LS, JSON.stringify({ x: Math.round(r.left), y: Math.round(r.top) }));
      } catch {
        /* ignore */
      }
    };
    /** @param {PointerEvent} e */
    const onDown = (e) => {
      if (/** @type {HTMLElement} */ (e.target).closest('.ol-popup__close')) return;
      const r = el.getBoundingClientRect();
      ox = e.clientX - r.left;
      oy = e.clientY - r.top;
      el.style.bottom = 'auto';
      el.classList.add('is-dragging');
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp, { once: true });
    };
    head.addEventListener('pointerdown', onDown);
    return () => {
      head.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
    };
  }

  // ======================================================================
  // Toast
  // ======================================================================
  const toastWrap = document.createElement('div');
  toastWrap.className = 'ol-toasts';
  // Vùng LIVE. Toast là kênh duy nhất báo "đang tải asset" / "tải lỗi"; không có
  // aria-live thì trình đọc màn hình hoàn toàn im lặng — người dùng không biết bài
  // đang chờ hay đã hỏng. `polite` chứ không `assertive`: thông báo tiến trình không
  // được cắt ngang câu đang đọc.
  toastWrap.setAttribute('role', 'status');
  toastWrap.setAttribute('aria-live', 'polite');
  // o.stage (KHÔNG phải o.root) — lý do giống toolRail/zoom/panelHost: toast phải
  // canh giữa đúng khung 3D đang co giãn (Whiteboard.js mở ra làm hẹp lại), không
  // phải canh giữa toàn màn hình. Neo theo root thì lúc mở Bảng trắng, chữ "Đang tải
  // tài nguyên…" vẫn hiện giữa TOÀN MÀN HÌNH — lệch hẳn khỏi khung nhìn model đã hẹp.
  o.stage.appendChild(toastWrap);

  /** @type {(() => void) | null} */
  let removeError = null;

  /** @type {UiApi} */
  const ui = {
    addButton(cfg) {
      const h = buttons.add(
        cfg.id,
        {
          icon: cfg.icon,
          labelKey: cfg.labelKey,
          toggle: cfg.toggle,
          state: cfg.initial ?? cfg.state,
          disabled: cfg.disabled,
          onClick: (/** @type {boolean|undefined} */ state) => {
            if (cfg.toggle) h.update({ state });
            cfg.onClick(state);
          },
        },
        cfg.order ?? 10,
      );
      return {
        setState: (v) => h.update({ state: v }),
        setEnabled: (v) => h.update({ disabled: !v }),
        remove: h.remove,
      };
    },

    addSlider(cfg) {
      const h = sliders.add(cfg.id, { ...cfg }, cfg.order ?? 10);
      return { setValue: (v) => h.update({ value: v }), remove: h.remove };
    },

    addTab(cfg) {
      const h = tabs.add(cfg.id, { ...cfg }, cfg.order ?? 10);
      // Trả cả `update` — GIỐNG addSlider ngay trên (trước đây chỉ addSlider có, đây
      // thiếu, không vì lý do gì). Cần cho tiết có nhãn bước KHÔNG đến từ dict i18n mà
      // đến từ dữ liệu runtime (vd extras vi/en đọc từ file .glb): đổi ngôn ngữ thì chỉ
      // cần update({labelKey}) tại chỗ, thay vì remove() rồi addTab() lại cả dãy —
      // cách đó làm StepBar dựng lại toàn bộ và có nhịp mất bước đang chọn.
      return { update: h.update, remove: h.remove };
    },

    /**
     * Vá thông tin bài NGAY LÚC CHẠY rồi vẽ lại đầu trang.
     *
     * Dùng cho nhánh "model mang mọi thứ": tên bài nằm ở node `TB` bên trong file .glb,
     * mà đầu trang đã vẽ xong từ trước khi model tải xong — không có đường này thì tiêu
     * đề đứng nguyên ở giá trị tạm (tên file .glb).
     * CHỈ VÁ Ô ĐANG TRỐNG: khối `lesson` của CMS/biên tập luôn thắng model, giống thứ tự
     * ưu tiên ba nguồn ở loadLessonData().
     * @param {Record<string, any>} patch
     */
    setLessonInfo(patch) {
      const tạm = new Set(o.info.placeholders ?? []);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null) continue;
        if (o.info[k] == null || o.info[k] === '' || tạm.has(k)) o.info[k] = v;
      }
      header.retranslate();
    },

    setAnimation(cfg) {
      animCbs = cfg ? { onToggle: cfg.onToggle, onSeek: cfg.onSeek } : null;
      animBar.show(
        cfg
          ? { duration: cfg.duration, playing: cfg.playing, mode: cfg.mode, stages: cfg.stages }
          : null,
      );
    },
    setAnimationPlaying(v) {
      animBar.setPlaying(v);
    },
    setAnimationProgress(v) {
      animBar.setProgress(v);
    },

    addInfoSection(cfg) {
      const h = sections.add(cfg.id, { ...cfg }, cfg.order ?? 20);
      return { remove: h.remove };
    },

    addSetting(cfg) {
      const h = extraSettings.add(cfg.id, { ...cfg }, cfg.order ?? 20);
      return { remove: h.remove };
    },

    addPopup,

    label: (cfg) => labelSystem.create(cfg),
    registerTextGroup: (id, h) => labelSystem.registerGroup(id, h),
    isTextVisible: () => labelSystem.isTextVisible(),
    // Khoá dãy bước trong lúc tiết nạp cảnh. Tiết PHẢI mở lại trong finally — xem
    // setMode() của tiết hải lưu.
    setStepsBusy: (on) => stepBar.setBusy(on),
    setQuiz: (source) => exercisePanel.setSource(source),

    toast(cfg) {
      const t = document.createElement('div');
      t.className = `ol-toast ol-toast--${cfg.kind ?? 'info'}`;
      t.textContent = o.t(cfg.messageKey);
      toastWrap.appendChild(t);
      setTimeout(() => t.remove(), cfg.ms ?? 2600);
    },

    setCompact(on) {
      stepBar.setCompact(on);
      toolRail.setCompact(on);
      zoom.setCompact(on);
      o.root.classList.toggle('is-compact', on);
      // Vào chế độ hẹp thì TẮT Bút + Bảng trắng: hai nút đó bị ẩn ở mobile (xem
      // @media max-width 720px trong shell.css). Nếu không tắt, người dùng đang bật Bút
      // ở màn rộng rồi thu nhỏ/quay ngang máy sẽ KẸT HẲN — lớp vẽ chặn toàn bộ pointer
      // nên không xoay/zoom mô hình được nữa, mà nút để tắt thì vừa bị ẩn mất.
      // Gọi kèm setPenActive/setWhiteboardActive để nút ở Header không lệch trạng thái
      // khi quay máy về ngang (đây là hai cửa ngõ NGƯỢC, không phát lại callback).
      if (on) {
        if (penLayer.isActive()) {
          penLayer.setActive(false);
          header.setPenActive(false);
        }
        if (whiteboard.isOpen()) {
          whiteboard.setOpen(false);
          header.setWhiteboardActive(false);
        }
      }
    },
  };

  return {
    ui,
    loading,
    stepBar,
    toolRail,
    infoPanel,
    settingsPanel,
    exercisePanel,

    /**
     * Bỏ cờ boot → các khối chrome trôi vào chỗ (CSS lo phần chuyển động, có so le).
     * Dùng cờ ÂM (`is-booting`, gỡ đi) chứ không phải cờ dương (`is-ready`, thêm vào):
     * nếu vì lý do nào đó không ai gọi hàm này thì chrome vẫn phải hiện, chứ không
     * được biến mất vĩnh viễn.
     */
    markReady() {
      o.root.classList.remove('is-booting');
    },

    /** @param {{code: string, message: string, retryable: boolean}} e */
    showError(e) {
      // Bài lỗi vẫn phải thấy taskbar (để đổi ngôn ngữ / mở thông tin).
      o.root.classList.remove('is-booting');
      removeError?.();
      removeError = showErrorScreen(o.root, e);
    },

    /** Dịch lại toàn bộ UI khi đổi ngôn ngữ runtime (setLanguage). */
    retranslate() {
      header.retranslate(); // tiêu đề bài + nhãn ba nút Bút/Bảng trắng/Bài tập
      penLayer.retranslate(); // nút "Xoá" + aria-label bảng màu
      whiteboard.retranslate(); // tiêu đề panel + nút đóng + nút "Xoá" + aria-label bảng màu
      stepBar.render(); // tiêu đề + phụ đề từng bước
      toolRail.render(); // chữ dưới icon của nút công cụ
      zoom.retranslate(); // tooltip + aria-label của thanh zoom
      infoPanel.retranslate();
      settingsPanel.retranslate();
      exercisePanel.retranslate();
      labelSystem.retranslate(); // TÊN nhãn 3D tạo bằng ctx.ui.label({key: ...})
      for (const p of popups.values()) p.retranslate(); // popup do TIẾT đăng ký
    },

    /** @param {number} w @param {number} h */
    onResize(w, h) {
      labelSystem.resize(w, h);
      ui.setCompact(w < 720);
    },

    dispose() {
      removeError?.();
      for (const p of popups.values()) {
        p.cleanup();
        p.el.remove();
      }
      popups.clear();
      labelSystem.dispose();
      settingsPanel.dispose();
      infoPanel.dispose();
      exercisePanel.dispose();
      panelHost.remove();
      loading.dispose();
      offSound();
      offRotate();
      offStageTheme();
      offZoomSync();
      zoom.dispose();
      toolRail.dispose();
      stepBar.dispose(); // gỡ luôn ảnh logo — nó là con của thanh này
      header.dispose();
      penLayer.dispose();
      whiteboard.dispose();
      toastWrap.remove();
      buttons.clear();
      sliders.clear();
      tabs.clear();
      sections.clear();
      extraSettings.clear();
    },
  };
}
